import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usersApiService } from '../../services/apiServices';
import { RoleName, UserStatus } from '../../api-client';

const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<{
    email: string;
    username: string;
    password: string;
    status: UserStatus;
    roles: RoleName[];
    fullName: string;
  }>({
    email: '',
    username: '',
    password: '',
    status: UserStatus.ACTIVE,
    roles: [],
    fullName: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    if (type === 'checkbox') {
      const role = value as RoleName;
      setFormData((prev) => ({
        ...prev,
        roles: checked ? [...prev.roles, role] : prev.roles.filter((r) => r !== role),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value as any,
      }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email обязателен';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email некорректен';
    }

    if (!formData.username) {
      newErrors.username = 'Имя пользователя обязательно';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Имя пользователя должно быть не менее 3 символов';
    }

    if (!formData.password) {
      newErrors.password = 'Пароль обязателен';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Пароль должен быть не менее 6 символов';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Формируем запрос с профилем, если указано ФИО
      const profileData = formData.fullName.trim()
        ? { full_name: formData.fullName.trim() }
        : undefined;

      await usersApiService.createUser(
        formData.email,
        formData.username,
        formData.password,
        formData.roles as any[],
        profileData
      );

      // Перенаправляем на страницу списка пользователей
      navigate('/users');
    } catch (err: any) {
      console.error('Ошибка при создании пользователя:', err);
      setError(err.message || 'Ошибка при создании пользователя');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Создание пользователя</h2>
            <Button variant="secondary" onClick={() => navigate('/users')}>
              Назад к списку
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <Row>
            <Col md={{ span: 8, offset: 2 }}>
              <Card>
                <Card.Header>
                  <Card.Title>Новый пользователь</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="formEmail">
                      <Form.Label>Электронная почта *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Введите email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formUsername">
                      <Form.Label>Имя пользователя *</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        placeholder="Введите имя пользователя"
                        value={formData.username}
                        onChange={handleChange}
                        isInvalid={!!errors.username}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.username}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formPassword">
                      <Form.Label>Пароль *</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        placeholder="Введите пароль"
                        value={formData.password}
                        onChange={handleChange}
                        isInvalid={!!errors.password}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formFullName">
                      <Form.Label>ФИО пользователя</Form.Label>
                      <Form.Control
                        type="text"
                        name="fullName"
                        placeholder="Введите ФИО пользователя"
                        value={formData.fullName}
                        onChange={handleChange}
                      />
                      <Form.Text className="text-muted">
                        Необязательное поле. Можно заполнить позже.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formStatus">
                      <Form.Label>Статус</Form.Label>
                      <Form.Select name="status" value={formData.status} onChange={handleChange}>
                        <option value="active">Активный</option>
                        <option value="pending">Ожидание</option>
                        <option value="blocked">Заблокирован</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formRoles">
                      <Form.Label>Роли</Form.Label>
                      <div className="d-grid gap-2">
                        <Form.Check
                          type="checkbox"
                          id="role-user"
                          label="Пользователь"
                          value={RoleName.USER}
                          checked={formData.roles.includes(RoleName.USER)}
                          onChange={handleChange}
                        />
                        <Form.Check
                          type="checkbox"
                          id="role-admin"
                          label="Администратор"
                          value={RoleName.ADMIN}
                          checked={formData.roles.includes(RoleName.ADMIN)}
                          onChange={handleChange}
                        />
                        <Form.Check
                          type="checkbox"
                          id="role-accountant"
                          label="Бухгалтер"
                          value={RoleName.ACCOUNTANT}
                          checked={formData.roles.includes(RoleName.ACCOUNTANT)}
                          onChange={handleChange}
                        />
                        <Form.Check
                          type="checkbox"
                          id="role-hr"
                          label="HR"
                          value={RoleName.HR}
                          checked={formData.roles.includes(RoleName.HR)}
                          onChange={handleChange}
                        />
                        <Form.Check
                          type="checkbox"
                          id="role-recruiter"
                          label="Рекрутер"
                          value={RoleName.RECRUITER}
                          checked={formData.roles.includes(RoleName.RECRUITER)}
                          onChange={handleChange}
                        />
                        <Form.Check
                          type="checkbox"
                          id="role-lawyer"
                          label="Юрист"
                          value={RoleName.LAWYER}
                          checked={formData.roles.includes(RoleName.LAWYER)}
                          onChange={handleChange}
                        />
                      </div>
                    </Form.Group>

                    <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => navigate('/users')}
                        disabled={loading}
                      >
                        Отмена
                      </Button>
                      <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              className="me-2"
                            />
                            Создание...
                          </>
                        ) : (
                          'Создать пользователя'
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default UserCreatePage;
